import { LANGUAGE_FALLBACK } from '@/features/common/languageFallback';
import type { LanguageFallback } from '@/features/common/types/LanguageFallback';
import type { Language } from '@/i18n/types';

/**
 * The language a public item arrives in. English only where the server says so: a payload with no
 * `contentLanguage` comes from a module nobody translates — trainings, funding measures — or from a
 * server that does not report it yet, and either way what it holds is Romanian.
 */
export function servedLanguage(item: { contentLanguage?: string | null }): Language {
  return item.contentLanguage === 'EN' ? 'en' : 'ro';
}

/**
 * The `lang` a text needs, or undefined when it is in the page's own language — so a screen reader
 * switches voice for a Romanian title on an English page (WCAG 3.1.2).
 */
export function textLang(served: Language, pageLanguage: Language): Language | undefined {
  return served === pageLanguage ? undefined : served;
}

/**
 * The `lang` one of an item's own texts needs on a page in `pageLanguage`. An item with an English
 * version can still carry a text nobody translated: the server serves that one in Romanian and
 * names it in `untranslatedFields`, so an English card can hold one Romanian line and say so.
 *
 * The rule is here rather than in each module because four of them now read it — events, content,
 * training sessions and funding measures — and the same three lines in four places is how two of
 * them come to disagree.
 */
export function itemTextLang(
  item: { contentLanguage?: string | null; untranslatedFields?: readonly string[] },
  field: string,
  pageLanguage: Language,
): Language | undefined {
  const served = item.untranslatedFields?.includes(field) ? 'ro' : servedLanguage(item);
  return textLang(served, pageLanguage);
}

/**
 * What the shell's notice says about a page, from the language each item it shows arrived in — one
 * for a detail page, every row for a listing. Items are translated one by one, so an English calendar
 * can hold both; the whole-page notice is kept for a page with nothing in English (T144). An empty
 * listing shows no Romanian text.
 */
export function languageFallbackOf(served: Language[], pageLanguage: Language): LanguageFallback {
  if (pageLanguage !== 'en') return LANGUAGE_FALLBACK.none;
  const romanian = served.filter((language) => language === 'ro').length;
  if (romanian === 0) return LANGUAGE_FALLBACK.none;
  return romanian === served.length ? LANGUAGE_FALLBACK.whole : LANGUAGE_FALLBACK.partial;
}
